// Step 4 — Inline quick-create.
//
// Only reachable via Step 3's "+ New campaign" action. Kind is
// inferred from wizard.path; product/callout ids auto-populate from
// Step 2 selections. Promotional path adds offer + endDate fields
// per the spec.
//
// The POST /api/campaigns endpoint accepts brand / product /
// promotional but NOT collection — so for the category path we render
// a note directing the operator back to Step 3 to pick an existing
// collection campaign. Wiring collection creation is a backend follow-up.

import { Box, VStack, HStack, Text, Input, Textarea, Badge } from '@chakra-ui/react';
import type { WizardSelections } from './types';

type Props = {
  value:    WizardSelections;
  onChange: (patch: Partial<WizardSelections>) => void;
};

export function Step4QuickCreate({ value, onChange }: Props) {
  const kind = value.path === 'category' ? 'collection' : value.path;
  const canCreate = kind === 'brand' || kind === 'product' || kind === 'promotional';

  if (!canCreate) {
    return (
      <Box borderWidth="1px" borderColor="orange.200" bg="orange.50" borderRadius="md" p={4}>
        <Text fontSize="sm" fontWeight="700" color="orange.700">
          Collection campaigns can't be created in-app yet
        </Text>
        <Text fontSize="xs" color="orange.700" mt={1}>
          Sync a Meta or Google collection campaign, or step back and pick an existing one.
        </Text>
      </Box>
    );
  }

  const set = (patch: Partial<WizardSelections['newCampaign']>) =>
    onChange({ newCampaign: { ...value.newCampaign, ...patch } });

  return (
    <VStack align="stretch" spacing={4}>
      <HStack spacing={2}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
          New {kind} campaign
        </Text>
        <Badge fontSize="9px" colorScheme="purple" variant="subtle">
          kind: {kind}
        </Badge>
      </HStack>

      <Box>
        <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1}>Name</Text>
        <Input
          size="sm"
          bg="white"
          value={value.newCampaign.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder={`e.g. ${kind === 'brand' ? 'Fall brand push' : kind === 'promotional' ? 'Black Friday 25% off' : 'Best sellers'}`}
        />
      </Box>

      <Box>
        <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1}>Description (optional)</Text>
        <Textarea
          size="sm"
          bg="white"
          rows={2}
          value={value.newCampaign.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Short brief — the storyboard uses this to steer copy direction."
        />
      </Box>

      {kind === 'promotional' && (
        <HStack spacing={4} align="flex-start">
          <Box flex={1}>
            <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1}>Offer</Text>
            <Input
              size="sm"
              bg="white"
              value={value.newCampaign.offer}
              onChange={(e) => set({ offer: e.target.value })}
              placeholder="e.g. 25% off site-wide"
            />
          </Box>
          <Box flex={1}>
            <Text fontSize="11px" fontWeight="700" color="brand.muted" mb={1}>End date</Text>
            <Input
              size="sm"
              type="date"
              bg="white"
              value={value.newCampaign.endDate}
              onChange={(e) => set({ endDate: e.target.value })}
            />
          </Box>
        </HStack>
      )}

      {kind === 'product' && (
        <Text fontSize="xs" color="brand.muted">
          {value.productIds.length} product{value.productIds.length === 1 ? '' : 's'} from Step 2 will be pinned to this campaign automatically.
        </Text>
      )}
      {kind === 'promotional' && value.calloutProductIds.length > 0 && (
        <Text fontSize="xs" color="brand.muted">
          {value.calloutProductIds.length} callout product{value.calloutProductIds.length === 1 ? '' : 's'} from Step 2 will be pinned.
        </Text>
      )}
    </VStack>
  );
}
